/* global CanvasImageSource, createImageBitmap */
// ============================================================
// imageDownscale (v8.363.9) - a picture no bigger than it needs to be.
//
// A phone photo is 12 MB and 4000 px wide; a handout on a screen is not.
// Downscaled to MAX_EDGE_PX on the long side and re-encoded before the
// upload, plus a small thumbnail for the cards and the vox. Feature-
// detected: where the runtime has no canvas (tests, an old browser) the
// original is returned untouched and the thumbnail is null.
// ============================================================
export interface Scaled {
  blob: Blob;
  width: number;
  height: number;
  /** True when the bytes were re-encoded; false when the original was kept. */
  scaled: boolean;
}

interface Drawer {
  decode(blob: Blob): Promise<{ width: number; height: number; source: CanvasImageSource; close?: () => void }>;
  encode(source: CanvasImageSource, width: number, height: number, type: string, quality: number): Promise<Blob | null>;
}

/** The browser's decoder and encoder; null where either is missing. */
export function browserDrawer(): Drawer | null {
  const g = globalThis as unknown as { createImageBitmap?: unknown; document?: { createElement?: unknown } };
  if (typeof g.createImageBitmap !== 'function' || typeof g.document?.createElement !== 'function') return null;
  return {
    async decode(blob) {
      const bmp = await createImageBitmap(blob);
      return { width: bmp.width, height: bmp.height, source: bmp, close: () => bmp.close() };
    },
    encode(source, width, height, type, quality) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return Promise.resolve(null);
      ctx.drawImage(source, 0, 0, width, height);
      return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
    },
  };
}

export function fitWithin(width: number, height: number, maxEdge: number): { width: number; height: number } {
  const edge = Math.max(width, height);
  if (edge <= maxEdge) return { width, height };
  const k = maxEdge / edge;
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

/** GIFs keep their bytes (a re-encode would freeze them); everything else becomes JPEG or WebP. */
export async function downscaleImage(
  blob: Blob,
  opts: { maxEdge: number; type?: 'image/jpeg' | 'image/webp'; quality?: number },
  drawer: Drawer | null = browserDrawer(),
): Promise<Scaled> {
  if (!drawer) return { blob, width: 0, height: 0, scaled: false };
  const decoded = await drawer.decode(blob);
  try {
    const fit = fitWithin(decoded.width, decoded.height, opts.maxEdge);
    const untouched = fit.width === decoded.width && fit.height === decoded.height;
    if (blob.type === 'image/gif' || (untouched && blob.size <= 2 * 1024 * 1024)) {
      return { blob, width: decoded.width, height: decoded.height, scaled: false };
    }
    const type = opts.type ?? (blob.type === 'image/png' ? 'image/webp' : 'image/jpeg');
    const out = await drawer.encode(decoded.source, fit.width, fit.height, type, opts.quality ?? 0.86);
    if (!out) return { blob, width: decoded.width, height: decoded.height, scaled: false };
    return { blob: out, width: fit.width, height: fit.height, scaled: true };
  } finally {
    decoded.close?.();
  }
}

/** A small JPEG for the cards; null where there is no canvas. */
export async function makeThumb(
  blob: Blob,
  edge: number,
  drawer: Drawer | null = browserDrawer(),
): Promise<Blob | null> {
  if (!drawer) return null;
  const decoded = await drawer.decode(blob);
  try {
    const fit = fitWithin(decoded.width, decoded.height, edge);
    return await drawer.encode(decoded.source, fit.width, fit.height, 'image/jpeg', 0.8);
  } finally {
    decoded.close?.();
  }
}
